/**
 * 🏢 WORKSPACE TYPES — Multi-Cliente / Multi-Organização
 * 
 * Define o contexto de workspace que isola dados por organização.
 * Admin/Consultor pode trocar de org; Gerente/Operador é fixo.
 */

export type UserRole =
  | 'admin'
  | 'consultant'
  | 'consultor_master'
  | 'manager'
  | 'gestor'
  | 'operator'
  | 'usuario';

export enum PermissionKey {
  CAN_CREATE_EXECUTION = 'CAN_CREATE_EXECUTION',
  CAN_REPROCESS_EXECUTION = 'CAN_REPROCESS_EXECUTION',
  CAN_PAUSE_EXECUTION = 'CAN_PAUSE_EXECUTION',
  CAN_CANCEL_EXECUTION = 'CAN_CANCEL_EXECUTION',
  CAN_VIEW_ANALYTICS = 'CAN_VIEW_ANALYTICS',
  CAN_VIEW_BLUEPRINT_PERFORMANCE = 'CAN_VIEW_BLUEPRINT_PERFORMANCE',
}

type PermissionState = Record<PermissionKey, boolean>;

export interface WorkspacePermissions extends PermissionState {
  canSwitchOrganization: boolean;
  canCreateProjects: boolean;
  canManageTeam: boolean;
  canViewAllProjects: boolean;
  canExportData: boolean;
  canConfigureTemplates: boolean;
  canCreateStealthProjects: boolean;
}

export interface Workspace {
  organizationId: string;
  organizationName: string;
  userRole: UserRole;
  permissions: WorkspacePermissions;
}

export interface OrganizationOption {
  id: string;
  name: string;
  logo?: string;
}

/** Calcula permissões baseado no role */
const basePermissionState: PermissionState = {
  [PermissionKey.CAN_CREATE_EXECUTION]: false,
  [PermissionKey.CAN_REPROCESS_EXECUTION]: false,
  [PermissionKey.CAN_PAUSE_EXECUTION]: false,
  [PermissionKey.CAN_CANCEL_EXECUTION]: false,
  [PermissionKey.CAN_VIEW_ANALYTICS]: false,
  [PermissionKey.CAN_VIEW_BLUEPRINT_PERFORMANCE]: false,
};

function buildPermissionState(overrides: Partial<PermissionState>): PermissionState {
  return { ...basePermissionState, ...overrides };
}

export function getPermissionsForRole(role: UserRole): WorkspacePermissions {
  switch (role) {
    case 'admin':
      return {
        ...buildPermissionState({
          [PermissionKey.CAN_CREATE_EXECUTION]: true,
          [PermissionKey.CAN_REPROCESS_EXECUTION]: true,
          [PermissionKey.CAN_PAUSE_EXECUTION]: true,
          [PermissionKey.CAN_CANCEL_EXECUTION]: true,
          [PermissionKey.CAN_VIEW_ANALYTICS]: true,
          [PermissionKey.CAN_VIEW_BLUEPRINT_PERFORMANCE]: true,
        }),
        canSwitchOrganization: true,
        canCreateProjects: true,
        canManageTeam: true,
        canViewAllProjects: true,
        canExportData: true,
        canConfigureTemplates: true,
        canCreateStealthProjects: true,
      };
    case 'consultant':
      return {
        ...buildPermissionState({
          [PermissionKey.CAN_CREATE_EXECUTION]: true,
          [PermissionKey.CAN_REPROCESS_EXECUTION]: true,
          [PermissionKey.CAN_PAUSE_EXECUTION]: true,
          [PermissionKey.CAN_CANCEL_EXECUTION]: true,
          [PermissionKey.CAN_VIEW_ANALYTICS]: true,
          [PermissionKey.CAN_VIEW_BLUEPRINT_PERFORMANCE]: false,
        }),
        canSwitchOrganization: true,
        canCreateProjects: true,
        canManageTeam: true,
        canViewAllProjects: true,
        canExportData: true,
        canConfigureTemplates: false,
        canCreateStealthProjects: true,
      };
    case 'consultor_master':
      return {
        ...buildPermissionState({
          [PermissionKey.CAN_CREATE_EXECUTION]: true,
          [PermissionKey.CAN_REPROCESS_EXECUTION]: true,
          [PermissionKey.CAN_PAUSE_EXECUTION]: true,
          [PermissionKey.CAN_CANCEL_EXECUTION]: true,
          [PermissionKey.CAN_VIEW_ANALYTICS]: true,
          [PermissionKey.CAN_VIEW_BLUEPRINT_PERFORMANCE]: false,
        }),
        canSwitchOrganization: true,
        canCreateProjects: true,
        canManageTeam: true,
        canViewAllProjects: true,
        canExportData: true,
        canConfigureTemplates: true,
        canCreateStealthProjects: true,
      };
    case 'manager':
    case 'gestor':
      return {
        ...buildPermissionState({
          [PermissionKey.CAN_CREATE_EXECUTION]: true,
          [PermissionKey.CAN_REPROCESS_EXECUTION]: true,
          [PermissionKey.CAN_PAUSE_EXECUTION]: true,
          [PermissionKey.CAN_CANCEL_EXECUTION]: true,
          [PermissionKey.CAN_VIEW_ANALYTICS]: true,
          [PermissionKey.CAN_VIEW_BLUEPRINT_PERFORMANCE]: true,
        }),
        canSwitchOrganization: false,
        canCreateProjects: true,
        canManageTeam: false,
        canViewAllProjects: true,
        canExportData: true,
        canConfigureTemplates: false,
        canCreateStealthProjects: false,
      };
    case 'operator':
    case 'usuario':
      return {
        ...buildPermissionState({
          [PermissionKey.CAN_CREATE_EXECUTION]: true,
          [PermissionKey.CAN_REPROCESS_EXECUTION]: false,
          [PermissionKey.CAN_PAUSE_EXECUTION]: true,
          [PermissionKey.CAN_CANCEL_EXECUTION]: false,
          [PermissionKey.CAN_VIEW_ANALYTICS]: false,
          [PermissionKey.CAN_VIEW_BLUEPRINT_PERFORMANCE]: false,
        }),
        canSwitchOrganization: false,
        canCreateProjects: false,
        canManageTeam: false,
        canViewAllProjects: false,
        canExportData: false,
        canConfigureTemplates: false,
        canCreateStealthProjects: false,
      };
  }
}
